import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Share, Plus } from 'lucide-react';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Check if opened from installed app (standalone mode) - don't show prompt
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) {
      return; // Opened from installed app, don't show
    }

    // Check if mobile browser
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) {
      return; // Desktop, don't show
    }

    // Detect platform
    const iOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const android = /Android/.test(navigator.userAgent);
    setIsIOS(iOS);
    setIsAndroid(android);

    // Check if we already captured the prompt (from index.html script)
    if (window.deferredPWAPrompt) {
      setDeferredPrompt(window.deferredPWAPrompt);
    }

    // Also listen for future events
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.deferredPWAPrompt = e;
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    // Show prompt after short delay
    const timer = setTimeout(() => setShowPrompt(true), 1500);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    // Get the prompt from state or window
    const prompt = deferredPrompt || window.deferredPWAPrompt;
    
    if (prompt) {
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        console.log('PWA install outcome:', outcome);
        if (outcome === 'accepted') {
          setShowPrompt(false);
        }
      } catch (err) {
        console.error('PWA install error:', err);
      }
      setDeferredPrompt(null);
      window.deferredPWAPrompt = null;
    } else {
      console.log('No install prompt available');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-slide-up">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#00966E] to-[#007a59] p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-md">
              <img src="/icons/icon-72x72.png" alt="Азбукарче" className="w-10 h-10 rounded-lg" />
            </div>
            <div className="text-white">
              <h3 className="font-bold text-lg">Азбукарче</h3>
              <p className="text-sm text-green-100">Добави към телефона</p>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10"
            aria-label="Затвори"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {isIOS ? (
            // iOS Instructions
            <div className="space-y-3">
              <p className="text-slate-600 text-sm font-medium">
                Добавете приложението към началния екран:
              </p>
              <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                <div className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="w-6 h-6 bg-[#00966E] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">1</span>
                  <div className="flex items-center gap-2">
                    <span>Натиснете</span>
                    <span className="inline-flex items-center gap-1 bg-slate-200 px-2 py-0.5 rounded">
                      <Share className="h-4 w-4" />
                      <span className="font-medium">Сподели</span>
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="w-6 h-6 bg-[#00966E] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">2</span>
                  <div className="flex items-center gap-2">
                    <span>Изберете</span>
                    <span className="inline-flex items-center gap-1 bg-slate-200 px-2 py-0.5 rounded">
                      <Plus className="h-4 w-4" />
                      <span className="font-medium">Към начален екран</span>
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="w-6 h-6 bg-[#00966E] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">3</span>
                  <span>Натиснете <strong>"Добави"</strong> горе вдясно</span>
                </div>
              </div>
              <Button 
                onClick={handleDismiss}
                className="w-full bg-[#00966E] hover:bg-[#007a59] text-white"
              >
                Разбрах
              </Button>
            </div>
          ) : isAndroid && (deferredPrompt || window.deferredPWAPrompt) ? (
            // Android with native prompt available
            <div className="space-y-3">
              <p className="text-slate-600 text-sm">
                Инсталирайте за бърз достъп от началния екран.
              </p>
              <div className="flex gap-2">
                <Button 
                  onClick={handleInstall}
                  className="flex-1 bg-[#00966E] hover:bg-[#007a59] text-white flex items-center justify-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Инсталирай
                </Button>
                <Button 
                  onClick={handleDismiss}
                  variant="outline"
                  className="border-slate-300"
                >
                  По-късно
                </Button>
              </div>
            </div>
          ) : (
            // Android without native prompt (fallback instructions)
            <div className="space-y-3">
              <p className="text-slate-600 text-sm font-medium">
                Добавете приложението към началния екран:
              </p>
              <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                <div className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="w-6 h-6 bg-[#00966E] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">1</span>
                  <span>Отворете менюто на браузъра <strong>(⋮)</strong></span>
                </div>
                <div className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="w-6 h-6 bg-[#00966E] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">2</span>
                  <span>Изберете <strong>"Добави към начален екран"</strong> или <strong>"Инсталирай приложение"</strong></span>
                </div>
                <div className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="w-6 h-6 bg-[#00966E] rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">3</span>
                  <span>Потвърдете с <strong>"Добави"</strong></span>
                </div>
              </div>
              <Button 
                onClick={handleDismiss}
                className="w-full bg-[#00966E] hover:bg-[#007a59] text-white"
              >
                Разбрах
              </Button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
